import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "react-phone-input-2/lib/material.css";
import { DealerLocation } from "../../models/features/Integration/DmsSetup";
import PhoneInput from "react-phone-input-2";

export interface FormValues {
  name: string;
  email: string;
  address: string;
  city: string;
  phone?: string;
  stateOrProvince: string;
  zipPostal: string;
}

// Validation schema
const LocationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string(),
  phone: Yup.string().matches(/^\+?\d{10,15}$/, "Enter a valid phone number"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("State or Province is required"),
  zipPostal: Yup.string().required("Zip/Postal is required"),
});

interface EditLocationDialogProps {
  openActualEditLocation: boolean;
  editLocation: DealerLocation | undefined;
  handleClose: () => void;
  handleUpdateLocation: (values: DealerLocation) => void;
  isLoading: boolean;
}

const EditLocationDialog: React.FC<EditLocationDialogProps> = ({
  openActualEditLocation,
  editLocation,
  handleClose,
  handleUpdateLocation,
  isLoading,
}) => {
  const initialValues: FormValues = {
    name: editLocation?.name || "",
    email: editLocation?.email || "",
    address: editLocation?.address || "",
    city: editLocation?.city || "",
    stateOrProvince: editLocation?.stateOrProvince || "",
    zipPostal: editLocation?.zipPostal || "",
    phone: editLocation?.phone || "",
  };

  const handleSave = (values: FormValues) => {
    if (editLocation) {
      let location: DealerLocation = {
        ...editLocation,
        name: values.name,
        email: values.email,
        address: values.address,
        city: values.city,
        stateOrProvince: values.stateOrProvince,
        zipPostal: values.zipPostal,
        phone: values.phone,
      };
      handleUpdateLocation(location);
    }
  };

  return (
    <Dialog open={openActualEditLocation} onClose={handleClose} fullWidth>
      <DialogTitle style={{ textAlign: "center" }} id="draggable-dialog-title">
        Edit Location
      </DialogTitle>
      <DialogContent sx={{ minHeight: "15vh" }}>
        <Formik
          initialValues={initialValues}
          validationSchema={LocationSchema}
          onSubmit={handleSave}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <Field
                as={TextField}
                label="Name"
                name="name"
                fullWidth
                margin="normal"
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
              />
              <Field
                as={TextField}
                label="Email"
                name="email"
                fullWidth
                margin="normal"
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
              />
              <Box mt={2}>
                <PhoneInput
                  country={"us"}
                  preferredCountries={["us", "ca"]}
                  value={values.phone}
                  onChange={(phone) => setFieldValue("phone", phone)}
                  inputStyle={{ width: "100%" }}
                  containerStyle={{ marginBottom: "16px" }}
                  inputProps={{
                    name: "phone",
                  }}
                />
              </Box>
              <ErrorMessage
                className="error-msg"
                name="phone"
                component={"dive"}
              />
              <Field
                as={TextField}
                label="Address"
                name="address"
                fullWidth
                margin="normal"
                error={touched.address && !!errors.address}
                helperText={touched.address && errors.address}
              />
              <Field
                as={TextField}
                label="City"
                name="city"
                fullWidth
                margin="normal"
                error={touched.city && !!errors.city}
                helperText={touched.city && errors.city}
              />
              <Field
                as={TextField}
                label="State or Province"
                name="stateOrProvince"
                fullWidth
                margin="normal"
                error={touched.stateOrProvince && !!errors.stateOrProvince}
                helperText={touched.stateOrProvince && errors.stateOrProvince}
              />
              <Field
                as={TextField}
                label="Zip/Postal"
                name="zipPostal"
                fullWidth
                margin="normal"
                error={touched.zipPostal && !!errors.zipPostal}
                helperText={touched.zipPostal && errors.zipPostal}
              />
              <DialogActions>
                <Button autoFocus variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  endIcon={
                    isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      <></>
                    )
                  }
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditLocationDialog;
